import { z } from 'zod'
import { logRequestFailure } from '@/helpers/request-outcome'
import { api } from '@/helpers/apiClient'
import { pageQuery } from '@/lib/pagination'

import {
  vendorDtoSchema,
  vendorListDtoSchema,
  vendorVenueListDtoSchema,
  vendorServiceApprovalListDtoSchema,
  nearbyVenueListDtoSchema,
  vendorRequestListDtoSchema,
  vendorLinkedVenueListDtoSchema,
  vendorSearchResultListDtoSchema,
  vendorRemovalPreviewDtoSchema,
  type VendorDto,
  type VendorListDto,
  type VendorVenueListDto,
  type VendorServiceApprovalListDto,
  type NearbyVenueListDto,
  type VendorRequestListDto,
  type VendorLinkedVenueListDto,
  type VendorSearchResultListDto,
  type VendorRemovalPreviewDto,
} from '@lib/dto/vendor.dto'
import {
  vendorUserDtoSchema,
  vendorUserListDtoSchema,
  type VendorUserDto,
  type VendorUserListDto,
} from '@lib/dto/vendor-user.dto'
import {
  vendorEventDtoSchema,
  vendorEventListDtoSchema,
  type VendorEventDto,
  type VendorEventListDto,
} from '@lib/dto/vendor-event.dto'
import type {
  AddVenueVendorInput,
  CreateVendorInput,
  InviteVendorUserInput,
  SetVendorSearchListingInput,
  UpdateVendorInput,
  VendorSearchQuery,
} from '@lib/input/vendor.input'
import type { VendorService as VendorServiceValue } from '@lib/enums/vendor-service.enum'
import {
  ownVendorProfileDtoSchema,
  profileChecklistDtoSchema,
  vendorProfileDtoSchema,
  type OwnVendorProfileDto,
  type ProfileChecklistDto,
  type VendorProfileDto,
} from '@lib/dto/vendor-profile.dto'
import { vendorDirectoryDtoSchema, type VendorDirectoryDto } from '@lib/dto/vendor-directory.dto'
import type { VendorDirectoryQuery } from '@lib/input/vendor-directory.input'
import type {
  AddVendorPhotoInput,
  PutVendorMediaLinksInput,
  ReorderVendorPhotosInput,
  UpdateVendorPhotoInput,
} from '@lib/input/vendor-profile.input'
import type { ReorderVendorOfferingsInput, VendorOfferingInput } from '@lib/input/vendor-offering.input'
import { vendorPublicPageSettingsDtoSchema, type VendorPublicPageSettingsDto } from '@lib/dto/vendor-public-page.dto'
import type { UpdateVendorPublicPageInput } from '@lib/input/vendor-public-page.input'

/**
 * Whose people: any vendor's, which only a super admin may reach, or the
 * signed-in vendor user's own. The same actions either way; only the address
 * differs, and the API decides who may take them.
 */
export type VendorPeopleScope = { kind: 'vendor'; vendorId: string } | { kind: 'own' }

/**
 * A company stopping work: at one venue, as its manager does it or a super
 * admin unlinking it, or everywhere, deactivated. Each is previewed first.
 */
export type VendorRemovalTarget =
  | { kind: 'venue'; venueId: string; vendorId: string }
  | { kind: 'unlink'; venueId: string; vendorId: string }
  | { kind: 'deactivate'; vendorId: string }

/**
 * Who recommends a company (docs/vendor-growth.spec.md, "Reputation"): a
 * venue, through its staff, or the signed-in planning company itself.
 */
export type RecommendAs = { as: 'venue'; venueId: string } | { as: 'planner' }

function recommendationPath(by: RecommendAs, vendorId: string): string {
  return by.as === 'venue' ? `/venues/${by.venueId}/vendor-recommendations/${vendorId}` : `/vendor/recommendations/${vendorId}`
}

function removalPreviewPath(target: VendorRemovalTarget): string {
  switch (target.kind) {
    case 'venue':
      return `/venues/${target.venueId}/vendors/${target.vendorId}/removal`
    case 'unlink':
      return `/vendors/${target.vendorId}/venues/${target.venueId}/removal`
    case 'deactivate':
      return `/vendors/${target.vendorId}/deactivation`
  }
}

function peoplePath(scope: VendorPeopleScope): string {
  return scope.kind === 'own' ? '/vendor/users' : `/vendors/${scope.vendorId}/users`
}

function scopeContext(scope: VendorPeopleScope): { vendorId: string } {
  return { vendorId: scope.kind === 'own' ? 'own' : scope.vendorId }
}

/**
 * Service for the vendor endpoints. The super-admin surface (`/vendors`) manages
 * vendors and their VENDOR users; the self-service surface (`/vendor`) is the
 * single vendor the signed-in vendor user belongs to. Responses are validated
 * against the shared `@lib` schemas at the boundary.
 */
class VendorService {
  // ---- super admin: vendors ----

  async list(page?: number): Promise<VendorListDto> {
    try {
      return await api.get(`/vendors${pageQuery(page)}`, vendorListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list vendors', {}, error)
      throw error
    }
  }

  async create(input: CreateVendorInput): Promise<VendorDto> {
    try {
      return await api.post('/vendors', input, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create vendor', {}, error)
      throw error
    }
  }

  async update(id: string, input: UpdateVendorInput): Promise<VendorDto> {
    try {
      return await api.patch(`/vendors/${id}`, input, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update vendor', { id }, error)
      throw error
    }
  }

  /** Super-admin override of a vendor's offered services. */
  async setServices(id: string, services: VendorServiceValue[]): Promise<VendorDto> {
    try {
      return await api.patch(`/vendors/${id}/services`, { services }, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set vendor services', { id }, error)
      throw error
    }
  }

  // ---- vendor self-service: your company's events ----

  /** Every event your company is on, across every venue and organization. */
  async listOwnEvents(): Promise<VendorEventListDto> {
    try {
      return await api.get('/vendor/events', vendorEventListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list vendor events', {}, error)
      throw error
    }
  }

  /** One event your company is on, read-only, as any vendor on it sees it. */
  async getOwnEvent(eventId: string): Promise<VendorEventDto> {
    try {
      return await api.get(`/vendor/events/${encodeURIComponent(eventId)}`, vendorEventDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load vendor event', { eventId }, error)
      throw error
    }
  }

  // ---- a vendor's people: any vendor's for a super admin, or your own ----

  async listPeople(scope: VendorPeopleScope): Promise<VendorUserListDto> {
    try {
      return await api.get(peoplePath(scope), vendorUserListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list vendor people', scopeContext(scope), error)
      throw error
    }
  }

  async invitePerson(scope: VendorPeopleScope, input: InviteVendorUserInput): Promise<VendorUserDto> {
    try {
      return await api.post(peoplePath(scope), input, vendorUserDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to invite vendor person', scopeContext(scope), error)
      throw error
    }
  }

  async deletePendingPerson(scope: VendorPeopleScope, userId: string): Promise<void> {
    try {
      await api.del(`${peoplePath(scope)}/${userId}`)
    } catch (error) {
      logRequestFailure('Failed to withdraw vendor invitation', { ...scopeContext(scope), userId }, error)
      throw error
    }
  }

  async deactivatePerson(scope: VendorPeopleScope, userId: string): Promise<VendorUserDto> {
    try {
      return await api.post(`${peoplePath(scope)}/${userId}/deactivate`, {}, vendorUserDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to deactivate vendor person', { ...scopeContext(scope), userId }, error)
      throw error
    }
  }

  async reactivatePerson(scope: VendorPeopleScope, userId: string): Promise<VendorUserDto> {
    try {
      return await api.post(`${peoplePath(scope)}/${userId}/reactivate`, {}, vendorUserDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reactivate vendor person', { ...scopeContext(scope), userId }, error)
      throw error
    }
  }

  async resendPersonInvite(scope: VendorPeopleScope, userId: string): Promise<void> {
    try {
      await api.post(`${peoplePath(scope)}/${userId}/resend-invite`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to resend vendor invitation', { ...scopeContext(scope), userId }, error)
      throw error
    }
  }

  /** Correct a pending owner's email and invite them again (super admin). */
  async correctPendingEmail(vendorId: string, userId: string, email: string): Promise<void> {
    try {
      await api.put(`/vendors/${vendorId}/users/${userId}/email`, { email }, z.null())
    } catch (error) {
      logRequestFailure('Failed to correct vendor person email', { vendorId, userId }, error)
      throw error
    }
  }

  /** Make someone an owner, or stop them being one. */
  async setPersonOwner(scope: VendorPeopleScope, userId: string, isOwner: boolean): Promise<VendorUserDto> {
    try {
      return await api.put(`${peoplePath(scope)}/${userId}/owner`, { isOwner }, vendorUserDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to change vendor owner', { ...scopeContext(scope), userId }, error)
      throw error
    }
  }

  // ---- super admin: a vendor's venue links ----

  /** Every venue (across tenants), each flagged with whether it's linked. */
  async listVenues(vendorId: string): Promise<VendorVenueListDto> {
    try {
      return await api.get(`/vendors/${vendorId}/venues`, vendorVenueListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list vendor venue options', { vendorId }, error)
      throw error
    }
  }

  async linkVenue(vendorId: string, venueId: string): Promise<void> {
    try {
      await api.post(`/vendors/${vendorId}/venues`, { venueId }, z.null())
    } catch (error) {
      logRequestFailure('Failed to link vendor to venue', { vendorId, venueId }, error)
      throw error
    }
  }

  async unlinkVenue(vendorId: string, venueId: string): Promise<void> {
    try {
      await api.del(`/vendors/${vendorId}/venues/${venueId}`)
    } catch (error) {
      logRequestFailure('Failed to unlink vendor from venue', { vendorId, venueId }, error)
      throw error
    }
  }

  /** What stopping work with a company would do, before it is done. */
  async removalPreview(target: VendorRemovalTarget): Promise<VendorRemovalPreviewDto> {
    try {
      return await api.get(removalPreviewPath(target), vendorRemovalPreviewDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to preview vendor removal', { kind: target.kind, vendorId: target.vendorId }, error)
      throw error
    }
  }

  /** A venue stops working with a company. Answers with the venue's vendors. */
  async stopAtVenue(venueId: string, vendorId: string): Promise<VendorServiceApprovalListDto> {
    try {
      return await api.delFor(`/venues/${venueId}/vendors/${vendorId}`, vendorServiceApprovalListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to stop working with vendor', { venueId, vendorId }, error)
      throw error
    }
  }

  /** Deactivate a company (super admin). A company is never deleted. */
  async deactivate(vendorId: string): Promise<VendorDto> {
    try {
      return await api.post(`/vendors/${vendorId}/deactivate`, {}, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to deactivate vendor', { vendorId }, error)
      throw error
    }
  }

  // ---- vendor self-service ----

  async getOwn(): Promise<VendorDto> {
    try {
      return await api.get('/vendor', vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load own vendor', {}, error)
      throw error
    }
  }

  async updateOwn(input: UpdateVendorInput): Promise<VendorDto> {
    try {
      return await api.patch('/vendor', input, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update own vendor', {}, error)
      throw error
    }
  }

  /** Set which services this vendor offers (vendor self-service). */
  async setOwnServices(services: VendorServiceValue[]): Promise<VendorDto> {
    try {
      return await api.patch('/vendor/services', { services }, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set own vendor services', {}, error)
      throw error
    }
  }

  /** Whether venues can find the signed-in user's company in search (owners only). */
  async setOwnSearchListing(input: SetVendorSearchListingInput): Promise<VendorDto> {
    try {
      return await api.put('/vendor/search-listing', input, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set own vendor search listing', { hidden: input.hidden }, error)
      throw error
    }
  }

  /** A company's profile, as venue staff and planners read it. */
  async getProfile(vendorId: string): Promise<VendorProfileDto> {
    try {
      return await api.get(`/vendor-profiles/${vendorId}`, vendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load a vendor profile', { vendorId }, error)
      throw error
    }
  }

  /** The event's venue's vendors, as its couple or planner browses them. */
  async vendorDirectory(eventId: string, query: VendorDirectoryQuery): Promise<VendorDirectoryDto> {
    const params = new URLSearchParams()
    if (query.q !== '') params.set('q', query.q)
    if (query.service !== undefined) params.set('service', query.service)
    if (query.unit !== undefined) params.set('unit', query.unit)
    if (query.maxPrice !== undefined) params.set('maxPrice', String(query.maxPrice))
    const search = params.toString()
    try {
      return await api.get(`/events/${eventId}/vendor-directory${search === '' ? '' : `?${search}`}`, vendorDirectoryDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s vendors', { eventId }, error)
      throw error
    }
  }

  /** Who at the signed-in company gets its quote requests by email; null for its owners. */
  async setQuoteRecipient(userId: string | null): Promise<VendorDto> {
    try {
      return await api.put('/vendor/quote-recipient', { userId }, vendorDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to choose who gets quote requests', {}, error)
      throw error
    }
  }

  /** Put the recommender's name on the company's profile. Recommending it again changes nothing. */
  async recommend(by: RecommendAs, vendorId: string): Promise<void> {
    try {
      await api.put(recommendationPath(by, vendorId), {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to recommend a vendor', { as: by.as, vendorId }, error)
      throw error
    }
  }

  async withdrawRecommendation(by: RecommendAs, vendorId: string): Promise<void> {
    try {
      await api.del(recommendationPath(by, vendorId))
    } catch (error) {
      logRequestFailure('Failed to take back a recommendation', { as: by.as, vendorId }, error)
      throw error
    }
  }

  // ---- the vendor's own profile (docs/vendor-growth.spec.md) ----

  /** What the company's profile still needs, step by step. */
  async getChecklist(): Promise<ProfileChecklistDto> {
    try {
      return await api.get('/vendor/checklist', profileChecklistDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the profile checklist', {}, error)
      throw error
    }
  }

  async getPublicPage(): Promise<VendorPublicPageSettingsDto> {
    try {
      return await api.get('/vendor/public-page', vendorPublicPageSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the company’s public page', {}, error)
      throw error
    }
  }

  async updatePublicPage(input: UpdateVendorPublicPageInput): Promise<VendorPublicPageSettingsDto> {
    try {
      return await api.put('/vendor/public-page', input, vendorPublicPageSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to publish or unpublish the company’s page', { on: input.on }, error)
      throw error
    }
  }

  async getOwnProfile(): Promise<OwnVendorProfileDto> {
    try {
      return await api.get('/vendor/profile', ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load own vendor profile', {}, error)
      throw error
    }
  }

  async addOwnPhoto(input: AddVendorPhotoInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.post('/vendor/photos', input, ownVendorProfileDtoSchema)
    } catch (error) {
      // No image in the breadcrumb: it can be a whole data URL.
      logRequestFailure('Failed to add a vendor photo', {}, error)
      throw error
    }
  }

  async updateOwnPhoto(photoId: string, input: UpdateVendorPhotoInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.patch(`/vendor/photos/${photoId}`, input, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update a vendor photo', { photoId }, error)
      throw error
    }
  }

  async deleteOwnPhoto(photoId: string): Promise<OwnVendorProfileDto> {
    try {
      return await api.delFor(`/vendor/photos/${photoId}`, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to remove a vendor photo', { photoId }, error)
      throw error
    }
  }

  async reorderOwnPhotos(input: ReorderVendorPhotosInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.put('/vendor/photos/order', input, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reorder vendor photos', {}, error)
      throw error
    }
  }

  async putOwnMediaLinks(input: PutVendorMediaLinksInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.put('/vendor/media-links', input, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save vendor video and audio links', { count: input.links.length }, error)
      throw error
    }
  }

  async createOwnOffering(input: VendorOfferingInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.post('/vendor/offerings', input, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to add a vendor package', { service: input.service }, error)
      throw error
    }
  }

  async replaceOwnOffering(offeringId: string, input: VendorOfferingInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.put(`/vendor/offerings/${offeringId}`, input, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update a vendor package', { offeringId }, error)
      throw error
    }
  }

  async deleteOwnOffering(offeringId: string): Promise<OwnVendorProfileDto> {
    try {
      return await api.delFor(`/vendor/offerings/${offeringId}`, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to remove a vendor package', { offeringId }, error)
      throw error
    }
  }

  async reorderOwnOfferings(input: ReorderVendorOfferingsInput): Promise<OwnVendorProfileDto> {
    try {
      return await api.put('/vendor/offerings/order', input, ownVendorProfileDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reorder vendor packages', {}, error)
      throw error
    }
  }

  /** Venues within this vendor's service radius, nearest first (for discovery). */
  async getNearbyVenues(): Promise<NearbyVenueListDto> {
    try {
      return await api.get('/vendor/nearby-venues', nearbyVenueListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load nearby venues', {}, error)
      throw error
    }
  }

  /** Request to serve a venue (creates a pending association). */
  async requestVenue(venueId: string): Promise<void> {
    try {
      await api.post('/vendor/venue-requests', { venueId }, z.null())
    } catch (error) {
      logRequestFailure('Failed to request venue', { venueId }, error)
      throw error
    }
  }

  // ---- venue side: vendor requests (tenant admin) ----

  async listVenueRequests(venueId: string): Promise<VendorRequestListDto> {
    try {
      return await api.get(`/venues/${venueId}/vendor-requests`, vendorRequestListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list venue vendor requests', { venueId }, error)
      throw error
    }
  }

  async acceptVenueRequest(venueId: string, vendorId: string): Promise<void> {
    try {
      await api.post(`/venues/${venueId}/vendor-requests/${vendorId}/accept`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to accept vendor request', { venueId, vendorId }, error)
      throw error
    }
  }

  async declineVenueRequest(venueId: string, vendorId: string): Promise<void> {
    try {
      await api.post(`/venues/${venueId}/vendor-requests/${vendorId}/decline`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to decline vendor request', { venueId, vendorId }, error)
      throw error
    }
  }

  // ---- venue side: per-service approvals (tenant admin) ----

  /** Vendors associated with a venue, with offered + approved services. */
  async listVenueVendorServices(venueId: string): Promise<VendorServiceApprovalListDto> {
    try {
      return await api.get(`/venues/${venueId}/vendor-services`, vendorServiceApprovalListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list venue vendor services', { venueId }, error)
      throw error
    }
  }

  /** Replace the venue's approved services for one vendor. */
  async setVenueVendorServices(venueId: string, vendorId: string, services: VendorServiceValue[]): Promise<void> {
    try {
      await api.put(`/venues/${venueId}/vendor-services/${vendorId}`, { services }, z.null())
    } catch (error) {
      logRequestFailure('Failed to set venue vendor services', { venueId, vendorId }, error)
      throw error
    }
  }

  /** Every company on the platform matching the search, as this venue's manager sees it. */
  async searchForVenue(venueId: string, query: VendorSearchQuery): Promise<VendorSearchResultListDto> {
    const params = new URLSearchParams({ q: query.q })
    if (query.service !== undefined) params.set('service', query.service)
    try {
      return await api.get(`/venues/${venueId}/vendors/search?${params.toString()}`, vendorSearchResultListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to search vendors', { venueId }, error)
      throw error
    }
  }

  /** Set a company up at a venue for one service — an existing one, or a new one and its owner. */
  async addToVenue(venueId: string, input: AddVenueVendorInput): Promise<VendorServiceApprovalListDto> {
    try {
      return await api.post(`/venues/${venueId}/vendors`, input, vendorServiceApprovalListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to add vendor to venue', { venueId }, error)
      throw error
    }
  }

  /** Correct (or resend to) the owner email of a company this venue set up, while they have not accepted. */
  async correctOwnerEmail(venueId: string, vendorId: string, email: string): Promise<void> {
    try {
      await api.put(`/venues/${venueId}/vendors/${vendorId}/owner-email`, { email }, z.null())
    } catch (error) {
      logRequestFailure('Failed to correct vendor owner email', { venueId, vendorId }, error)
      throw error
    }
  }

  /** The venues a super admin has linked this vendor to (read-only). */
  async getOwnVenues(): Promise<VendorLinkedVenueListDto> {
    try {
      return await api.get('/vendor/venues', vendorLinkedVenueListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load own vendor venues', {}, error)
      throw error
    }
  }
}

export const vendorService = new VendorService()
